import { BR_POOL, AR_POOL, agree, type Gender, type Noun, type Pool } from './pools.ts';

export type Rng = () => number;
export type Template = 'animal' | 'pet' | 'food' | 'title' | 't5' | 'regional';

/** Leaves room for the `.NN` collision suffix inside the 20-char username limit. */
export const USERNAME_BASE_MAX_LEN = 17;

const WEIGHTS: Array<[Template, number]> = [
  ['animal', 30], ['pet', 15], ['food', 10], ['title', 15], ['t5', 15], ['regional', 15],
];
const WEIGHT_TOTAL = WEIGHTS.reduce((sum, [, w]) => sum + w, 0);

/** Registration placeholder alphabet — NormalUserRegistrationService.USERNAME_ALPHABET. */
const PLACEHOLDER_ALPHABET = '23456789abcdefghjkmnpqrstuvwxyz';

function pick<T>(list: readonly T[], rng: Rng): T {
  return list[Math.min(list.length - 1, Math.floor(rng() * list.length))];
}

function pickTemplate(rng: Rng): Template {
  let roll = rng() * WEIGHT_TOTAL;
  for (const [template, weight] of WEIGHTS) {
    roll -= weight;
    if (roll < 0) return template;
  }
  return WEIGHTS[WEIGHTS.length - 1][0];
}

function nounWithAdjective(noun: Noun, adjectives: Pool['adjectives'], rng: Rng): string {
  return `${noun.word}.${agree(pick(adjectives, rng), noun.g)}`;
}

export function generateFromTemplate(pool: Pool, template: Template, rng: Rng): string {
  switch (template) {
    case 'animal':
      return nounWithAdjective(pick(pool.animals, rng), pool.adjectives, rng);
    case 'pet':
      return nounWithAdjective(pick(pool.petForms, rng), pool.adjectives, rng);
    case 'food':
      return nounWithAdjective(pick(pool.foods, rng), pool.foodAdjectives, rng);
    case 'title': {
      if (rng() < 0.5) return nounWithAdjective(pick(pool.persons, rng), pool.adjectives, rng);
      const noun = pick([...pool.animals, ...pool.foods], rng);
      const titles = [...pool.titles.any, ...pool.titles[noun.g]];
      return `${pick(titles, rng)}.${noun.word}`;
    }
    case 't5': {
      const combos = (pool.t5Titles.m.length + pool.t5Titles.f.length) * (pool.t5Things.m.length + pool.t5Things.f.length);
      if (rng() < pool.idioms.length / (pool.idioms.length + combos)) return pick(pool.idioms, rng);
      const titleGender: Gender = rng() < 0.5 ? 'm' : 'f';
      const thingGender: Gender = rng() < 0.5 ? 'm' : 'f';
      return `${pick(pool.t5Titles[titleGender], rng)}.${pool.t5Article[thingGender]}.${pick(pool.t5Things[thingGender], rng)}`;
    }
    case 'regional':
      return nounWithAdjective(pick(pool.animals, rng), pool.regionals, rng);
  }
}

/** A clean, digit-free handle of at most 17 characters. */
export function generateUsername(pool: Pool, rng: Rng = Math.random): string {
  for (;;) {
    const name = generateFromTemplate(pool, pickTemplate(rng), rng);
    if (name.length <= USERNAME_BASE_MAX_LEN) return name;
  }
}

export function poolForAccount(user: { country_code?: string } | null | undefined, uiLanguage: string): Pool {
  const code = (user?.country_code || '').toUpperCase();
  if (code === 'AR') return AR_POOL;
  if (code === 'BR') return BR_POOL;
  if (!code && uiLanguage.toLowerCase().startsWith('es')) return AR_POOL;
  return BR_POOL;
}

/** The `<cc>` + 6-char placeholder registration writes before the player picks a name. */
export function isGeneratedUsername(value: string, countryCode?: string | null): boolean {
  const cc = (countryCode || 'br').toLowerCase();
  const pattern = new RegExp(`^${cc}[${PLACEHOLDER_ALPHABET}]{6}$`, 'i');
  return pattern.test(value.trim());
}
